import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface TargetAudienceDialogProps {
  targetAudience: string;
  onSave: (audience: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  defaultAudience?: string; // From selected writing style
}

export function TargetAudienceDialog({
  targetAudience,
  onSave,
  open,
  onOpenChange,
  children,
  defaultAudience
}: TargetAudienceDialogProps) {
  const [localAudience, setLocalAudience] = useState(targetAudience);

  useEffect(() => {
    if (open) {
      setLocalAudience(targetAudience);
    }
  }, [open, targetAudience]);

  const handleSave = () => {
    onSave(localAudience.trim());
    onOpenChange(false);
  };

  const handleUseDefault = () => {
    if (defaultAudience) {
      setLocalAudience(defaultAudience);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <DialogContent className="max-w-[90vw] sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Target Audience</DialogTitle>
          <DialogDescription>
            Define who you're writing for. This helps tailor AI suggestions and analysis to your specific audience.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="audience">Audience Description</Label>
            <Textarea
              id="audience"
              placeholder="e.g., B2B SaaS decision-makers, tech-savvy millennials, small business owners..."
              value={localAudience}
              onChange={(e) => setLocalAudience(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Describe demographics, interests, pain points, or any relevant characteristics.
            </p>
          </div>

          {defaultAudience && defaultAudience !== localAudience && (
            <div className="rounded-lg border border-muted bg-muted/50 p-3">
              <p className="text-sm font-medium mb-2">Default from writing style:</p>
              <p className="text-sm text-muted-foreground mb-2">{defaultAudience}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleUseDefault}
                className="w-full"
              >
                Use Default Audience
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Audience
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
